const normalizeConceptualLabel = conceptualLabel => ({
    id: conceptualLabel.id,
    name: conceptualLabel.name,
    description: conceptualLabel.description,
    disciplineId: conceptualLabel.disciplines?.[0]?.id,
    source: conceptualLabel.source,
    creator: conceptualLabel.creator,
    createdAt: conceptualLabel.created_at,
    modifiedAt: conceptualLabel.modified_at,
    modifierId: conceptualLabel.modified_by_id,
    parentId: conceptualLabel.parent_id,
    typeId: conceptualLabel.type_id,
    visualId: conceptualLabel.visual_id,
    isSystem: conceptualLabel.is_system,
})

// Function to build a map of normalized conceptual labels by ID
const buildConceptualLabelMap = response => {
    return response.reduce((map, label) => {
        map[label.id] = normalizeConceptualLabel(label)
        return map
    }, {})
}

// Function to group child labels under their parent
const assignChildrenToParents = (labelMap, response) => {
    response.forEach(({ id, parent_id }) => {
        if (parent_id && labelMap[parent_id]) {
            const parent = labelMap[parent_id]
            parent.children = parent.children || []
            parent.children.push(labelMap[id])
        }
    })

    // Sort children by visualId for each parent
    Object.values(labelMap).forEach(label => {
        if (label.children && label.children.length > 0) {
            label.children.sort((a, b) => a.visualId - b.visualId)
        }
    })
}

// Function to build ordered hierarchy and sort by visualId
const buildOrderedHierarchy = (response, labelMap) => {
    return response
        .map(({ id, parent_id }) => {
            if (parent_id) return null // Skip child labels as they are already nested
            const label = labelMap[id]
            if (label.children && label.children.length === 0) {
                delete label.children // Remove empty children array for non-parent labels
            }
            return label
        })
        .filter(Boolean) // Remove skipped child labels
        .sort((a, b) => a.visualId - b.visualId) // Sort top-level labels by visualId
}

export const normalizeFetchConceptualLabelsResponse = response => {
    if (!response || !Array.isArray(response)) return []

    const labelMap = buildConceptualLabelMap(response) // Normalize all labels
    assignChildrenToParents(labelMap, response) // Nest child labels under parents
    return buildOrderedHierarchy(response, labelMap) // Retain order and clean structure
}

export const normalizeCreateConceptualLabelResponse = response => normalizeConceptualLabel(response)

export const normalizeUpdateConceptualLabelResponse = response => normalizeConceptualLabel(response)

export const normalizeFetchConceptualLabelDiscoveryResponse = response => {
    if (!response) {
        return response
    }

    return {
        ...response,
        patterns: response.patterns.map(pattern => ({
            ...pattern,
            criteria: pattern.criteria.map((criterion, criterionIndex) => ({
                value: criterion,
                id: String(criterionIndex + 1),
            })),
            // Map proposed_conceptual_labels to conceptual_labels for frontend compatibility
            conceptual_labels: pattern.proposed_conceptual_labels || [],
        })),
    }
}

export const normalizeFetchConceptualLabelUsageResponse = (response, conceptualLabelId) => {
    if (!Array.isArray(response)) {
        // If response is a single object (not array), normalize it directly
        return {
            conceptualLabelId: parseInt(conceptualLabelId),
            patternsCount: response.patterns_count || 0,
            groupsCount: response.groups_count || 0,
            categoriesCount: response.categories_count || 0,
            totalUsage: response.total_usage || 0
        }
    }

    // Find the usage data for the specific conceptual label from array
    const usageData = response.find(item => item.conceptual_label_id === parseInt(conceptualLabelId))
    
    if (!usageData) {
        return {
            conceptualLabelId: parseInt(conceptualLabelId),
            patternsCount: 0,
            groupsCount: 0,
            categoriesCount: 0,
            totalUsage: 0
        }
    }

    // Transform snake_case to camelCase
    return {
        conceptualLabelId: usageData.conceptual_label_id,
        patternsCount: usageData.patterns_count,
        groupsCount: usageData.groups_count,
        categoriesCount: usageData.categories_count,
        totalUsage: usageData.total_usage
    }
}
